import React, { useState } from 'react';
import { 
  BookOpen, 
  CheckCircle2, 
  ExternalLink, 
  Clock, 
  PoundSterling, 
  Zap, 
  Award, 
  CheckSquare, 
  Square, 
  TrendingUp,
  Target,
  Users
} from 'lucide-react';

const CONTRACT_PLATFORMS = [
  {
    name: 'Jobserve UK',
    type: 'High-Volume IT Contracts',
    rate: '£300 - £550 / day',
    speed: '2 - 5 days',
    url: 'https://www.jobserve.com/gb/en/Job-Search/',
    tip: 'Filter by "Contract", sort by "Posted: Today", and call the agency recruiter on the phone immediately after applying.'
  },
  {
    name: 'YunoJuno',
    type: 'Vetted UK Freelance & Tech',
    rate: '£350 - £600 / day',
    speed: '3 - 7 days',
    url: 'https://www.yunojuno.com/',
    tip: 'Leading UK platform for creative & tech contracts. Outside IR35 compliance handled automatically.'
  },
  {
    name: 'Cord.co',
    type: 'Direct Tech Leads (No Recruiters)',
    rate: '£55k - £90k / Contract',
    speed: '48 hours response',
    url: 'https://cord.co/',
    tip: 'Direct messaging with Heads of Engineering and CTOs at UK startups. Bypasses HR queues.'
  },
  {
    name: 'Worksome',
    type: 'Enterprise Contractor Matching',
    rate: '£350 - £500 / day',
    speed: '3 - 7 days',
    url: 'https://www.worksome.com/',
    tip: 'Direct hiring platform for UK companies looking for fast contractor onboarding.'
  },
  {
    name: 'Otta (Welcome to the Jungle)',
    type: 'Top Tech Companies & Scaleups',
    rate: '£60k - £95k / High-growth',
    speed: '1 - 2 weeks',
    url: 'https://app.otta.com/',
    tip: 'Curated roles with transparent salary and tech stack filters.'
  }
];

export const FastCashPlaybook: React.FC = () => {
  const [checkedTasks, setCheckedTasks] = useState<Record<string, boolean>>({
    task1: false,
    task2: false,
    task3: false,
    task4: false,
    task5: false,
  });

  const toggleTask = (id: string) => {
    setCheckedTasks(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="space-y-10 pb-16">
      
      {/* Header */}
      <div className="border-b border-slate-800/80 pb-6">
        <div className="inline-flex items-center space-x-2 px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold">
          <PoundSterling className="w-3.5 h-3.5" />
          <span>UK Cash-Flow Blueprint</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white mt-1">14-Day Fast Revenue & Contractor Playbook</h1>
        <p className="text-xs sm:text-sm text-slate-400">
          How to turn your IT skills into £2,000 – £5,000 monthly income without waiting months for slow corporate hiring.
        </p>
      </div>

      {/* 3 Pillars of Immediate Revenue */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-6 space-y-4 shadow-lg">
          <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center text-sky-400 font-bold">
            1
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Agency Overflow Dev</h3>
            <span className="text-xs font-semibold text-sky-400">Goal: £1,200 - £2,500 in 7 days</span>
          </div>
          <p className="text-xs text-slate-300 leading-relaxed">
            UK digital & web design agencies are constantly overloaded with client requests. Pitching directly to agency Technical Directors as an on-demand contractor clears their sprint backlogs with zero overhead.
          </p>
          <div className="pt-2 text-[11px] text-slate-400 border-t border-slate-800">
            <strong>Target:</strong> 15 agency pitches/day → 1-2 contract bookings per week.
          </div>
        </div>

        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-6 space-y-4 shadow-lg">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 font-bold">
            2
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Immediate Contract Boards</h3>
            <span className="text-xs font-semibold text-amber-400">Goal: £300 - £450 / day</span>
          </div>
          <p className="text-xs text-slate-300 leading-relaxed">
            Avoid generic job boards (Indeed/LinkedIn Easy Apply). Focus strictly on contractor boards where recruitment agencies have urgent client mandates starting on Monday.
          </p>
          <div className="pt-2 text-[11px] text-slate-400 border-t border-slate-800">
            <strong>Target:</strong> Jobserve, YunoJuno, Cord.co. Always phone the agent.
          </div>
        </div>

        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-6 space-y-4 shadow-lg">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">
            3
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Automation Retainers</h3>
            <span className="text-xs font-semibold text-emerald-400">Goal: £2k - £3k / mo Recurring</span>
          </div>
          <p className="text-xs text-slate-300 leading-relaxed">
            Set up open-source n8n or webhooks for UK SMEs (lead routing, invoice sync, CRM alerts). Charge £850 setup + £350/mo support retainer. 4-6 clients = £2,000/mo steady recurring revenue.
          </p>
          <div className="pt-2 text-[11px] text-slate-400 border-t border-slate-800">
            <strong>Target:</strong> Local accountants, estate agents, solicitors, e-com.
          </div>
        </div>

      </div>

      {/* Direct UK Contractor Platforms List */}
      <div className="rounded-3xl bg-slate-900 border border-slate-800 p-6 sm:p-8 space-y-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">Top High-Velocity UK Contractor Platforms</h3>
            <p className="text-xs text-slate-400">Direct links to start applying for urgent, high-rate roles today</p>
          </div>
          <span className="text-xs text-sky-400 font-semibold bg-sky-500/10 px-3 py-1 rounded-full border border-sky-500/20">
            Active UK Hiring
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CONTRACT_PLATFORMS.map((platform, idx) => (
            <div key={idx} className="rounded-2xl bg-slate-950/70 border border-slate-800 p-5 space-y-3 flex flex-col justify-between hover:border-slate-700 transition-all">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold text-white">{platform.name}</h4>
                  <span className="text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                    {platform.speed}
                  </span>
                </div>
                <div className="text-xs text-sky-300 font-semibold">{platform.rate}</div>
                <p className="text-[11px] text-slate-400 leading-relaxed">{platform.tip}</p>
              </div>

              <a
                href={platform.url}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center justify-center space-x-1.5 w-full py-2 px-3 rounded-xl text-xs font-semibold bg-slate-800 hover:bg-sky-500 hover:text-white text-slate-200 transition-all"
              >
                <span>Browse {platform.name}</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* The Daily 90-Minute Revenue Routine */}
      <div className="rounded-3xl bg-gradient-to-r from-slate-900 via-slate-900 to-indigo-950/40 border border-slate-800 p-6 sm:p-8 space-y-6 shadow-xl">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">The Daily 90-Minute High-Yield Prospecting Routine</h3>
            <p className="text-xs text-slate-400">Complete this exact routine every morning from Monday to Friday</p>
          </div>
        </div>

        <div className="space-y-3">
          
          <div
            onClick={() => toggleTask('task1')}
            className="flex items-start space-x-3 p-3.5 rounded-2xl bg-slate-950/80 border border-slate-800/80 cursor-pointer hover:border-slate-700 transition-all"
          >
            <div className="mt-0.5 text-sky-400">
              {checkedTasks.task1 ? <CheckSquare className="w-5 h-5 text-emerald-400" /> : <Square className="w-5 h-5 text-slate-600" />}
            </div>
            <div className="space-y-0.5">
              <div className={`text-xs font-bold ${checkedTasks.task1 ? 'line-through text-slate-500' : 'text-white'}`}>
                1. 09:00 - 09:20 | Scan Jobserve for "Contracts posted in last 24h"
              </div>
              <p className="text-[11px] text-slate-400">
                Identify 3 urgent contracts. Call or email the recruitment consultant directly mentioning immediate availability.
              </p>
            </div>
          </div>

          <div
            onClick={() => toggleTask('task2')}
            className="flex items-start space-x-3 p-3.5 rounded-2xl bg-slate-950/80 border border-slate-800/80 cursor-pointer hover:border-slate-700 transition-all"
          >
            <div className="mt-0.5 text-sky-400">
              {checkedTasks.task2 ? <CheckSquare className="w-5 h-5 text-emerald-400" /> : <Square className="w-5 h-5 text-slate-600" />}
            </div>
            <div className="space-y-0.5">
              <div className={`text-xs font-bold ${checkedTasks.task2 ? 'line-through text-slate-500' : 'text-white'}`}>
                2. 09:20 - 09:50 | Send 5 Agency Overflow Cold Pitches via Direct Outreach Tab
              </div>
              <p className="text-[11px] text-slate-400">
                Search Google Maps / LinkedIn for "Web agency London / Manchester / Leeds". Message the Technical Director using the 100-word Overflow Dev script.
              </p>
            </div>
          </div>

          <div
            onClick={() => toggleTask('task3')}
            className="flex items-start space-x-3 p-3.5 rounded-2xl bg-slate-950/80 border border-slate-800/80 cursor-pointer hover:border-slate-700 transition-all"
          >
            <div className="mt-0.5 text-sky-400">
              {checkedTasks.task3 ? <CheckSquare className="w-5 h-5 text-emerald-400" /> : <Square className="w-5 h-5 text-slate-600" />}
            </div>
            <div className="space-y-0.5">
              <div className={`text-xs font-bold ${checkedTasks.task3 ? 'line-through text-slate-500' : 'text-white'}`}>
                3. 09:50 - 10:15 | Tailor 2 High-Value Roles with AI CV Tailor
              </div>
              <p className="text-[11px] text-slate-400">
                Paste the target job description into the AI CV Tailor tab, extract missing keywords, and generate a 95%+ ATS matching CV.
              </p>
            </div>
          </div>

          <div
            onClick={() => toggleTask('task4')}
            className="flex items-start space-x-3 p-3.5 rounded-2xl bg-slate-950/80 border border-slate-800/80 cursor-pointer hover:border-slate-700 transition-all"
          >
            <div className="mt-0.5 text-sky-400">
              {checkedTasks.task4 ? <CheckSquare className="w-5 h-5 text-emerald-400" /> : <Square className="w-5 h-5 text-slate-600" />}
            </div>
            <div className="space-y-0.5">
              <div className={`text-xs font-bold ${checkedTasks.task4 ? 'line-through text-slate-500' : 'text-white'}`}>
                4. 10:15 - 10:30 | Follow up on all pending conversations in Pipeline
              </div>
              <p className="text-[11px] text-slate-400">
                Move cards in your Application Pipeline and send a quick 1-line check-in to any manager who hasn't replied in 3 days.
              </p>
            </div>
          </div>

        </div>
      </div>

    </div>
  );
};
